import Head from 'next/head';

import { ReactElement, useEffect, useState } from 'react';

import { ColorModeScript } from '@chakra-ui/react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { setDefaultOptions } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { ErrorBoundary } from 'components/ErrorBoundary';
import { AppLayout } from 'components/layouts';
import { theme, ThemeProvider } from 'components/theme';
import { Loader } from 'components/uikit';
import { ActionModal } from 'components/uikit/ActionModal';
import { useAccessGuard } from 'data/auth/hooks/useAccessGuard';
import { COMPANY_NAME } from 'data/constants';
import { dateFnsDefaultConfig } from 'data/constants/dateFns';
import 'locales/i18n';
import { useGetInitialColorMode } from 'utils/hooks/useGetInitialColorMode';
import { useSyncHtmlLang } from 'utils/hooks/useSyncHtmlLang';
import { AppPropsWithLayout, EntryPoint } from 'utils/nextTypes';
import {
  isCurrentNotificationPermissionDefault,
  requestNotificationPermission,
  setNotificationPermissionDenied,
} from 'utils/notifications';

setDefaultOptions(dateFnsDefaultConfig);

function getNonce() {
  if (typeof document === 'undefined') return undefined;
  return (
    document.querySelector('meta[name="csp-nonce"]')?.getAttribute('content') ??
    undefined
  );
}

const emotionCache = createCache({
  key: 'css',
  nonce: getNonce(),
});

function AppHead() {
  return (
    <Head>
      <title>{COMPANY_NAME}</title>
      {/* <!-- Primary Meta Tags --> */}
      {/* <!-- https://developer.mozilla.org/en-US/docs/Web/HTML/Element/meta/name/theme-color --> */}
      <meta
        name='theme-color'
        media='(prefers-color-scheme: light)'
        content='#EEF2F6'
      />
      <meta
        name='theme-color'
        media='(prefers-color-scheme: dark)'
        content='#090D13'
      />
      <link
        rel='apple-touch-icon'
        sizes='180x180'
        href={`/static/${COMPANY_NAME}/favicon/apple-touch-icon.png`}
      />
      <link
        rel='icon'
        type='image/png'
        sizes='32x32'
        href={`/static/${COMPANY_NAME}/favicon/favicon-32x32.ico`}
      />
      <link
        rel='icon'
        type='image/png'
        sizes='16x16'
        href={`/static/${COMPANY_NAME}/favicon/favicon-16x16.ico`}
      />
      <link
        rel='manifest'
        href={`/static/favicon/${COMPANY_NAME}/site.webmanifest`}
      />
    </Head>
  );
}

const defaultPageRenderer = (page: ReactElement) => page;
const loaderRenderer = () => <Loader />;

interface AppProps extends AppPropsWithLayout {
  userLoaded: boolean;
  initialAccessPath?: string;
}

export function App({
  Component,
  pageProps,
  userLoaded,
  initialAccessPath,
}: AppProps) {
  const [isMounted, setIsMounted] = useState(false);
  useGetInitialColorMode();
  useSyncHtmlLang();

  useAccessGuard(Component.entryPointType, initialAccessPath);
  const { t } = useTranslation('account');

  const getLayout =
    !userLoaded && Component.entryPointType === EntryPoint.PROTECTED
      ? loaderRenderer
      : Component.getLayout ?? defaultPageRenderer;

  const [
    isNotificationPermissionPopupShown,
    setNotificationPermissionPopupShown,
  ] = useState(false);

  const getRequestNotificationModal =
    !userLoaded || Component.entryPointType !== EntryPoint.PROTECTED ? (
      ''
    ) : (
      <ActionModal
        isOpen={userLoaded && isNotificationPermissionPopupShown}
        onClose={() => {
          setNotificationPermissionPopupShown(false);
        }}
        onOverlayClick={() => setNotificationPermissionPopupShown(false)}
        title={t('push_notification_permission_title')}
        actions={[
          {
            content: t('push_notification_permission_allow'),
            onClick: async () => {
              await requestNotificationPermission();
              setNotificationPermissionPopupShown(false);
            },
          },
          {
            content: t('push_notification_permission_deny'),
            onClick: () => {
              setNotificationPermissionPopupShown(false);
              setNotificationPermissionDenied();
            },
          },
        ]}
      />
    );

  useEffect(() => {
    setIsMounted(true);
    setNotificationPermissionPopupShown(
      isCurrentNotificationPermissionDefault(),
    );
  }, []);

  if (!isMounted) {
    return loaderRenderer();
  }

  return (
    <CacheProvider value={emotionCache}>
      <ThemeProvider>
        <AppHead />
        <AppLayout>
          <ErrorBoundary>
            <ColorModeScript initialColorMode={theme.config.initialColorMode} />
            {getLayout(<Component {...pageProps} />)}
            {getRequestNotificationModal}
          </ErrorBoundary>
        </AppLayout>
      </ThemeProvider>
    </CacheProvider>
  );
}
